import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getHomepageConfig, updateHomepageConfig } from '../../services/homepage.service';
import FeaturedManager from './homepage/FeaturedManager';
import CampaignsManager from './homepage/CampaignsManager';
import DealManager from './homepage/DealManager';
import DealsPageManager from './homepage/DealsPageManager';
import { Loader2, Save, ShoppingBag, Star, Zap, Percent, LayoutGrid } from 'lucide-react';

const HomepageManager = ({ products }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState({
        featuredProducts: [],
        campaigns: [],
        dealOfDay: {},
        dealsPage: [],
        newArrivalsProductIds: [], // New
        bestSellersProductIds: [] // New
    });
    const [activeTab, setActiveTab] = useState('featured');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const config = await getHomepageConfig();
            setData({
                featuredProducts: config.featuredProducts || [],
                campaigns: config.campaigns || [],
                dealOfDay: config.dealOfDay || {},
                dealsPage: config.dealsPage || [],
                newArrivalsProductIds: config.newArrivalsProductIds || [],
                bestSellersProductIds: config.bestSellersProductIds || []
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateHomepageConfig(data);
            alert(t('admin.messages.saved'));
        } catch (err) {
            alert(t('admin.messages.error'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-green-600 w-8 h-8" /></div>;

    const tabs = [
        { id: 'featured', label: t('admin.homepage.tabs.featured'), icon: Star },
        { id: 'newArrivals', label: "Yeni Gelenler", icon: ShoppingBag }, // Translation key needed later
        { id: 'bestSellers', label: "En Çok Satanlar", icon: LayoutGrid }, // Translation key needed later
        { id: 'campaigns', label: t('admin.homepage.tabs.campaigns'), icon: Zap },
        { id: 'deal', label: t('admin.homepage.tabs.deal'), icon: Percent },
    ];

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2 bg-gray-100 p-1 rounded-xl">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === tab.id
                                ? 'bg-white text-green-700 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 bg-green-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-green-700 transition-colors disabled:opacity-70 shadow-lg shadow-green-200"
                >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    {t('admin.actions.save')}
                </button>
            </div>

            <div className="min-h-[500px] bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">

                {activeTab === 'featured' && (
                    <FeaturedManager
                        featuredProducts={data.featuredProducts || []}
                        onChange={(newVal) => setData({ ...data, featuredProducts: newVal })}
                        allProducts={products || []}
                    />
                )}

                {activeTab === 'newArrivals' && (
                    <FeaturedManager // Reusing FeaturedManager as Logic is identical (Select Products -> Order)
                        featuredProducts={data.newArrivalsProductIds?.map(id => ({ id })) || []}
                        onChange={(newVal) => setData({ ...data, newArrivalsProductIds: newVal.map(i => i.id) })}
                        allProducts={products || []}
                        title="Yeni Gelenler Listesi"
                        description="Buradan 'Yeni Gelenler' bölümünde gösterilecek ürünleri seçip sıralayabilirsiniz. Boş bırakırsanız otomatik olarak en yeni eklenen ürünler gösterilir."
                    />
                )}

                {activeTab === 'bestSellers' && (
                    <FeaturedManager // Reusing FeaturedManager logic
                        featuredProducts={data.bestSellersProductIds?.map(id => ({ id })) || []}
                        onChange={(newVal) => setData({ ...data, bestSellersProductIds: newVal.map(i => i.id) })}
                        allProducts={products || []}
                        title="En Çok Satanlar Listesi"
                        description="Buradan 'En Çok Satanlar' bölümünde gösterilecek ürünleri seçip sıralayabilirsiniz. Boş bırakırsanız otomatik mantık çalışır."
                    />
                )}

                {activeTab === 'campaigns' && (
                    <CampaignsManager
                        campaigns={data.campaigns || []}
                        onChange={(newVal) => setData({ ...data, campaigns: newVal })}
                        allProducts={products || []}
                    />
                )}

                {activeTab === 'deal' && (
                    <DealManager
                        dealData={data.dealOfDay || {}}
                        onChange={(field, val) => setData({ ...data, dealOfDay: { ...data.dealOfDay, [field]: val } })}
                        allProducts={products || []}
                    />
                )}
            </div>
        </div>
    );
};

export default HomepageManager;
